import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command/Command";
import { Label } from "@/components/ui/Label/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover/Popover";
import { SelectStyles } from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { normalize } from "@/utils/normalize";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDownIcon, Circle, OctagonX } from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useExplorations } from "../../hooks/explorations/useExplorations";
import { gradeFromGradYear } from "@/utils/gradeFromGradYear";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";

const schema = z.object({
  students: z
    .array(z.string("Student must be a string").min(1, "A student is required"))
    .min(1, "At least one student is required")
    .default([]),
});

export type EnrollStudentsFormInput = z.input<typeof schema>;
export type EnrollStudentsFormOutput = z.output<typeof schema>;

const EnrollStudentsForm = ({
  exploration,
  students,
  onSuccess,
}: {
  exploration: any;
  students: any[];
  onSuccess: () => void;
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EnrollStudentsFormInput, unknown, EnrollStudentsFormOutput>({
    resolver: zodResolver(schema),
  });

  const { enrollStudents } = useExplorations();

  const onSubmit: SubmitHandler<EnrollStudentsFormOutput> = async (data) => {
    await enrollStudents(data.students, exploration._id, onSuccess);
  };

  return (
    <>
      <SheetBody>
        <form id="enroll-students-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Students</Label>
                {errors.students && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.students.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="students"
                render={({ field }) => {
                  const [open, setOpen] = useState<boolean>(false);
                  const [gradeFilter, setGradeFilter] = useState<Set<number>>(
                    new Set(),
                  );
                  const [selectedOnly, setSelectedOnly] =
                    useState<boolean>(false);
                  const selected = field.value ?? [];

                  const { grades, idsByGrade } = useMemo(() => {
                    const map = new Map<number, string[]>();

                    for (const s of students) {
                      const grade = Number(gradeFromGradYear(s.gradYear));
                      if (Number.isNaN(grade)) continue;
                      const list = map.get(grade) ?? [];
                      list.push(String(s._id));
                      map.set(grade, list);
                    }

                    const grades = Array.from(map.keys()).sort((a, b) => a - b);
                    return { grades, idsByGrade: map };
                  }, [students]);

                  const filteredStudents = useMemo(() => {
                    return students.filter((s: any) => {
                      if (gradeFilter.size > 0) {
                        const g = Number(gradeFromGradYear(s.gradYear));
                        if (!gradeFilter.has(g)) return false;
                      }
                      if (selectedOnly && !selected.includes(String(s._id))) {
                        return false;
                      }
                      return true;
                    });
                  }, [students, gradeFilter, selectedOnly, selected]);

                  const toggleGradeFilter = (grade: number) => {
                    setGradeFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(grade)) next.delete(grade);
                      else next.add(grade);
                      return next;
                    });
                  };

                  const getGradeState = (
                    grade: number,
                  ): "none" | "some" | "all" => {
                    const ids = idsByGrade.get(grade) ?? [];
                    if (ids.length === 0) return "none";

                    let count = 0;
                    for (const id of ids) if (selected.includes(id)) count++;

                    if (count === 0) return "none";
                    if (count === ids.length) return "all";
                    return "some";
                  };

                  const toggleGrade = (grade: number) => {
                    const ids = idsByGrade.get(grade) ?? [];
                    if (ids.length === 0) return;

                    const state = getGradeState(grade);

                    if (state === "all") {
                      field.onChange(
                        selected.filter((id) => !ids.includes(id)),
                      );
                      return;
                    }

                    const next = new Set(selected);
                    for (const id of ids) next.add(id);
                    field.onChange(Array.from(next));
                  };

                  const fullGrades = useMemo(() => {
                    const out: number[] = [];
                    for (const g of grades) {
                      if (getGradeState(g) === "all") out.push(g);
                    }
                    return out;
                  }, [grades, selected, idsByGrade]);

                  const gradeLabel = (() => {
                    if (fullGrades.length === 0) return "Select by grade";
                    if (fullGrades.length === 1)
                      return `Grade ${fullGrades[0]}`;
                    return `${fullGrades.length} grades selected`;
                  })();

                  const label = (() => {
                    if (selected.length === 0) return "Select students";
                    if (selected.length === 1) {
                      const one = students.find(
                        (s: any) => s._id === selected[0],
                      );
                      return one?.name ?? "1 student selected";
                    }
                    return `${selected.length} students selected`;
                  })();

                  const toggle = (id: string) => {
                    const next = selected.includes(id)
                      ? selected.filter((x) => x !== id)
                      : [...selected, id];

                    field.onChange(next);
                  };

                  return (
                    <VStack>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            id="date"
                            aria-invalid={errors.students ? true : false}
                            className={SelectStyles.trigger}
                            {...((fullGrades.length === 0 || !field.value) && {
                              "data-placeholder": "",
                            })}
                          >
                            {gradeLabel}
                            <ChevronDownIcon
                              className={SelectStyles.triggerIcon}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          style={{
                            width: "var(--radix-dropdown-menu-trigger-width)",
                          }}
                        >
                          {grades.map((g) => {
                            const state = getGradeState(g);

                            return (
                              <DropdownMenuItem
                                key={g}
                                onSelect={(e) => {
                                  e.preventDefault();
                                  toggleGrade(g);
                                }}
                              >
                                <HStack
                                  align="center"
                                  justify="space-between"
                                  style={{ width: "100%" }}
                                >
                                  {g}th Grade
                                  {state === "some" ? (
                                    <Circle
                                      style={{
                                        width: "1rem",
                                        height: "1rem",
                                        color: "var(--secondary-foreground)",
                                      }}
                                    />
                                  ) : state === "all" ? (
                                    <Check
                                      style={{
                                        width: "1rem",
                                        height: "1rem",
                                        color: "var(--secondary-foreground)",
                                      }}
                                    />
                                  ) : null}
                                </HStack>
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            id="date"
                            aria-invalid={errors.students ? true : false}
                            className={SelectStyles.trigger}
                            {...((field.value?.length === 0 ||
                              !field.value) && {
                              "data-placeholder": "",
                            })}
                          >
                            {label}
                            <ChevronDownIcon
                              className={SelectStyles.triggerIcon}
                            />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          style={{
                            width: "var(--radix-popover-trigger-width)",
                            overflow: "hidden",
                            padding: 0,
                          }}
                          align="start"
                        >
                          <Command
                            filter={(itemValue, search) => {
                              const value = normalize(itemValue);
                              const query = normalize(search);
                              if (!query) return 1;
                              return value.includes(query) ? 1 : 0;
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "0.25rem",
                                padding: "0.5rem",
                                borderBottom: "1px solid var(--border)",
                              }}
                            >
                              {grades.map((g) => {
                                const active = gradeFilter.has(g);
                                return (
                                  <Button
                                    key={g}
                                    type="button"
                                    size="extra-small"
                                    variant={active ? "secondary" : "outline"}
                                    onClick={() => toggleGradeFilter(g)}
                                  >
                                    {g}th
                                  </Button>
                                );
                              })}
                              <Button
                                type="button"
                                size="extra-small"
                                variant={selectedOnly ? "secondary" : "outline"}
                                disabled={selected.length === 0}
                                onClick={() => setSelectedOnly((v) => !v)}
                              >
                                Selected only
                              </Button>
                            </div>
                            <CommandInput placeholder="Search students..." />
                            <CommandList>
                              <CommandEmpty>No students found.</CommandEmpty>
                              <CommandGroup>
                                {filteredStudents.map((s: any) => {
                                  const isSelected = selected.includes(s._id);

                                  return (
                                    <CommandItem
                                      key={s._id}
                                      value={`${s.name} - ${gradeFromGradYear(s.gradYear)}th grade`}
                                      onSelect={() => {
                                        toggle(s._id);
                                      }}
                                    >
                                      <HStack
                                        align="center"
                                        justify="space-between"
                                        style={{ width: "100%" }}
                                      >
                                        {s.name} -{" "}
                                        {gradeFromGradYear(s.gradYear)}
                                        th grade
                                        {isSelected && (
                                          <Check
                                            style={{
                                              width: "1rem",
                                              height: "1rem",
                                              color:
                                                "var(--secondary-foreground)",
                                            }}
                                          />
                                        )}
                                      </HStack>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </VStack>
                  );
                }}
              />
            </VStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="enroll-students-form"
          style={{ width: "100%" }}
        >
          Enroll Students
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { EnrollStudentsForm };
